import React, { useEffect, useImperativeHandle, useState } from 'react'
import { useFormik, FormikProvider } from 'formik'
import { useModal } from 'providers/modal-provider'
import { CODE, CONDITION_DETAIL } from 'domains/product-attribute/constants'
import { defaultSchema, dynamicSchema } from './seller-market-product-main-attribute-form-schema'
import ProductAttributeSelectField from 'domains/product-attribute/molecules/select-field'
import ProductCategorySelectField from 'domains/product-category/molecules/select-field'
import CurrencyInputField from 'components/molecules/currency-input-field'
import ProductAttributeDynamicFields from 'domains/product-attribute/organisms/dynamic-fields'
import GenericModal from 'components/organisms/generic-modal'
import PropTypes from 'prop-types'
import withLoader from 'hocs/with-loader'
import SellerSelectSearchField from 'domains/seller/molecules/select-search-field/seller-select-search-field'
import { PROCESS_STATUS } from 'domains/seller-market-product/constants'

/**
 * memoized component to prevent unecessary rendering.
 * They will render again if the component props has changed
 */
const MemoizedConditionFormText = React.memo(({ memoizedConditionModalToggle }) => (
  <>
    Klik <span style={{ color: '#f28ca9', cursor: 'pointer' }} onClick={ memoizedConditionModalToggle }> disini</span> untuk
    panduan lebih lanjut
  </>
))

MemoizedConditionFormText.displayName = 'MemoizedConditionFormText'
MemoizedConditionFormText.propTypes   = {
  memoizedConditionModalToggle: PropTypes.func
}

const MemoizedConditionModal = React.memo(({ ...props }) => (
  <GenericModal
    title={ 'PRODUCT CONDITION' }
    { ...props }
  >
    {
      CONDITION_DETAIL.map((condition, index) => (
        <div key={ `condition_detail_${ index }` }>
          <div>
            <div className='font-weight-bold mb-3'>
              { condition.name }
            </div>

            <div className='mb-4'>
              <span>
                { 'Produk dengan kondisi' }
              </span>

              <span>
                { ` ${ condition.name } ` }
              </span>

              { condition.detail }
            </div>
          </div>
        </div>
      ))
    }
  </GenericModal>
))

MemoizedConditionModal.displayName = 'MemoizedConditionModal'


const propTypes = {
  refId             : PropTypes.object,
  onChange          : PropTypes.func,
  attributes        : PropTypes.array,
  formData          : PropTypes.object,
  disabledFields    : PropTypes.object,
  onFetchAttributes : PropTypes.func
}

const defaultProps = {
  formData: {
    mainAttributeForm: {
      initialValues: {
        [CODE.CATEGORY]  : '',
        [CODE.CONDITION] : '',
        [CODE.PRICE]     : '',
        [CODE.BRAND]     : ''
      }
    }
  },
  isAlreadyLive  : false,
  disabledFields : {}
}

/**
 * Market Product upload image form.
 * this component use imperative way to submit.
 * the submit button should be handle on the parent componet
 * @param {string} refId - Reference ID from useRef
 * @param {func} onChange - Handler to change form value state in the parent
 * @param {array} attributes - collection of Product Attribute
 * @param {object} initialValues - Formik initial values
 * @param {object} disabledFields - Flag to disable particular fields
 * @param {func} onFetchAttributes - handler to fetch attributes when category has been changed
 */
const MarketProductMainAttributeForm = ({
  refId,
  onChange,
  attributes,
  formData,
  disabledFields,
  onFetchAttributes
}) => {
  const [ validationSchema, setValidationSchema ] = useState(defaultSchema)

  const initialValues = formData.mainAttributeForm.initialValues
  const formik        = useFormik({
    initialValues      : initialValues,
    enableReinitialize : false,
    validationSchema   : validationSchema,
    onSubmit           : async () => await watchForm()
  })
  const { showModal } = useModal()
  const {
    values: {
      category
    }
  } = formik

  useEffect(() => {
    /** triggered when category has been changed */
    if (category) {
      if (onFetchAttributes) onFetchAttributes(category)
    }
  }, [ category, onFetchAttributes ])

  useImperativeHandle(refId, () => ({
    submit    : async () => await formik.submitForm(),
    reload    : () => reload(),
    reset     : () => formik.resetForm(),
    status    : formik.status,
    setStatus : (status) => formik.setStatus(status),
    isValid   : formik.isValid
  }))


  /**
   * Trigger to change validation schema and values depending on the attributes props
   * @param {bool} isNewValues - Flag whether it should change new values or not
   */
  const reload = () => {

    const generatedValidationSchema = dynamicSchema({ attributes })
    setValidationSchema(generatedValidationSchema)
  }

  /** Let the parent to 'watch' the form values when submitting */
  const watchForm = () => {
    if (onChange) {
      onChange({
        values: {
          ...formik.values
        }
      })
    }
  }

  /** Memoized modal toggle */
  const memoizedConditionModalToggle = () => showModal(MemoizedConditionModal)

  const isSellerFieldDisabled   = formData.process_status !== PROCESS_STATUS.INCOMPLETE
  const isCategoryFieldDisabled = formData.process_status !== PROCESS_STATUS.INCOMPLETE && disabledFields?.category

  return (
    <FormikProvider value={ formik }>
      <form ref={ refId }>
        <SellerSelectSearchField
          name='seller_id'
          label='Seller'
          isDisabled={ isSellerFieldDisabled }
          required={ true }
        />

        <ProductCategorySelectField
          name='category'
          label='Kategori'
          required={ true }
          formText={ 'Kamu tidak bisa mengubah kategori setelah produknya sudah dibuat' }
          isDisabled={ isCategoryFieldDisabled }
        />

        <ProductAttributeSelectField
          code={ CODE.CONDITION }
          name={ CODE.CONDITION }
          label={ 'Kondisi' }
          isCuration={ true }
          isMulti={ false }
          required={ true }
          formText={ <MemoizedConditionFormText memoizedConditionModalToggle={ memoizedConditionModalToggle }/> }
        />

        <ProductAttributeSelectField
          code={ CODE.BRAND }
          name={ CODE.BRAND }
          label={ 'Brand' }
          isCuration={ true }
          isMulti={ false }
          required={ true }
        />

        <CurrencyInputField
          name={ CODE.PRICE }
          label={ 'Harga' }
          required={ true }
          disabled
        />

        <ProductAttributeDynamicFields attributes={ attributes } />
      </form>
    </FormikProvider>
  )
}

MarketProductMainAttributeForm.propTypes    = propTypes
MarketProductMainAttributeForm.defaultProps = defaultProps

export default withLoader(MarketProductMainAttributeForm)
